import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface AddressValue {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export function emptyAddress(): AddressValue {
  return { addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "", country: "" };
}

export function addressFromApi(a?: { addressLine1?: string; addressLine2?: string; city?: string; state?: string; postalCode?: string; country?: string } | null): AddressValue {
  return {
    addressLine1: a?.addressLine1 ?? "",
    addressLine2: a?.addressLine2 ?? "",
    city: a?.city ?? "",
    state: a?.state ?? "",
    postalCode: a?.postalCode ?? "",
    country: a?.country ?? "",
  };
}

export function addressToPayload(a: AddressValue): { addressLine1?: string; addressLine2?: string; city?: string; state?: string; postalCode?: string; country?: string } | undefined {
  const any = a.addressLine1 || a.addressLine2 || a.city || a.state || a.postalCode || a.country;
  if (!any) return undefined;
  return {
    addressLine1: a.addressLine1 || undefined,
    addressLine2: a.addressLine2 || undefined,
    city: a.city || undefined,
    state: a.state || undefined,
    postalCode: a.postalCode || undefined,
    country: a.country || undefined,
  };
}

export function formatAddressDisplay(a?: { addressLine1?: string; addressLine2?: string; city?: string; state?: string; postalCode?: string; country?: string } | null): string {
  if (!a) return "";
  const lines: string[] = [];
  if (a.addressLine1) lines.push(a.addressLine1);
  if (a.addressLine2) lines.push(a.addressLine2);
  const cityState = [a.city, a.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, a.postalCode].filter(Boolean).join(" ");
  if (cityStateZip) lines.push(cityStateZip);
  if (a.country) lines.push(a.country);
  return lines.join(", ");
}

interface AddressInputProps {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  idPrefix?: string;
  labelPrefix?: string;
}

export function AddressInput({ value, onChange, idPrefix = "addr", labelPrefix = "" }: AddressInputProps) {
  const set = (field: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [field]: e.target.value });

  const label = labelPrefix ? `${labelPrefix} ` : "";

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-line1`}>{label}Address Line 1</Label>
        <Input
          id={`${idPrefix}-line1`}
          value={value.addressLine1}
          onChange={set("addressLine1")}
          placeholder="123 Main St"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-line2`}>{label}Address Line 2</Label>
        <Input
          id={`${idPrefix}-line2`}
          value={value.addressLine2}
          onChange={set("addressLine2")}
          placeholder="Suite 400"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-city`}>City</Label>
          <Input
            id={`${idPrefix}-city`}
            value={value.city}
            onChange={set("city")}
            placeholder="San Francisco"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-state`}>State / Province</Label>
          <Input
            id={`${idPrefix}-state`}
            value={value.state}
            onChange={set("state")}
            placeholder="CA"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-postal`}>Postal Code</Label>
          <Input
            id={`${idPrefix}-postal`}
            value={value.postalCode}
            onChange={set("postalCode")}
            placeholder="94105"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-country`}>Country</Label>
          <Input
            id={`${idPrefix}-country`}
            value={value.country}
            onChange={set("country")}
            placeholder="US"
          />
        </div>
      </div>
    </div>
  );
}
